import React, { useState, useEffect } from "react";
import { createActivity, updateActivity, fetchActivityTypes } from "../api/activityService";
import UserSearchDropdown from "../../../common/components/UserSearchDropdown"; // Adjust the path as needed

function AddEditActivityModal({ isOpen, onClose, isEditing, activity, leadId, opportunityId }) {
  const [formData, setFormData] = useState({
    activity_id: "",
    opportunity_id: opportunityId || "",
    lead_id: leadId || "",
    user_id: "",
    branch_id: "",
    organization_id: "",
    activity_type_id: "",
    activity_status_id: "1",
    description: "",
    scheduled_at: "",
    completed_at: null,
    assigned_to: "",
    notes: "",
    is_follow_up: false,
    follow_up_date: "",
    follow_up_time: "",
  });

  const [activityTypes, setActivityTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEditing && activity) {
      setFormData((prev) => ({
        ...prev,
        activity_id: activity.activity_id || "",
        opportunity_id: activity.opportunity_id || opportunityId || "",
        lead_id: activity.lead_id !== undefined && activity.lead_id !== null ? activity.lead_id : leadId || prev.lead_id || null,
        user_id: activity.user_id || "",
        branch_id: activity.branch_id || "",
        organization_id: activity.organization_id || "",
        activity_type_id: activity.activity_type_id || "",
        activity_status_id: activity.activity_status_id || "1",
        description: activity.description || "",
        scheduled_at: activity.scheduled_at || "",
        completed_at: activity.completed_at || null,
        assigned_to: activity.assigned_to || "",
        notes: activity.notes || "",
        is_follow_up: activity.is_follow_up || false,
        follow_up_date: activity.follow_up_date || "",
        follow_up_time: activity.follow_up_time || "",
      }));
    }
    else {
      setFormData((prev) => ({
        ...prev,
        lead_id: leadId || prev.lead_id || null, // Ensure lead_id is set when creating a new activity
      }));
    }
  }, [isEditing, activity,leadId, opportunityId]);

  useEffect(() => {
    const getActivityTypes = async () => {
      try {
        const types = await fetchActivityTypes();
        setActivityTypes(Array.isArray(types) ? types : []);
      } catch (error) {
        console.error("Failed to fetch activity types:", error);
      }
    };

    if (isOpen) {
      getActivityTypes();
    }
  }, [isOpen]);

  const handleUserSelect = (selectedUser) => {
    setFormData((prev) => ({
      ...prev,
      assigned_to: selectedUser.user_id, // Update the assigned_to field with the selected user's ID
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: 
        type === "checkbox" ? checked : 
        (name === "activity_type_id" || name === "activity_status_id") ? parseInt(value, 10) || null : 
        value === "" ? null : value, // ✅ Convert empty strings to null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!formData.activity_type_id || !formData.description || !formData.scheduled_at) {
      alert("Please fill in all required fields.");
      return;
    }

    if (formData.is_follow_up && !formData.follow_up_date) {
      alert("Please provide a follow-up date.");
      return;
    }

    

  // ✅ Convert empty strings to null and ensure activity_status_id is an integer
  const processedFormData = Object.fromEntries(
    Object.entries(formData).map(([key, value]) => [
      key, 
      key === "activity_status_id" || key === "activity_type_id" ? parseInt(value, 10) || null : 
      key === "lead_id" ? value || leadId || null : // ✅ Ensure lead_id is preserved
      value === "" ? null : value
    ])
  );

    try {
      setIsSubmitting(true);

      if (isEditing) {
        await updateActivity(activity.activity_id, processedFormData);
      } else {
        const { activity_id, ...newActivityData } = processedFormData;
        await createActivity(newActivityData);
      }

      onClose();
    } catch (error) {
      console.error("Error saving activity:", error);
      alert("An error occurred while saving the activity. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl flex flex-col h-[90vh]">
        <div className="px-6 py-4 border-b bg-gray-100 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-700">
            {isEditing ? "Edit Activity" : "Add Activity"}
          </h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-900 text-2xl">
            &times;
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Activity Type <span className="text-red-500">*</span>
                </label>
                <select name="activity_type_id" value={formData.activity_type_id} onChange={handleChange}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select Type</option>
                  {activityTypes.map((type) => (
                    <option key={type.activity_type_id} value={type.activity_type_id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description <span className="text-red-500">*</span>
                </label>
                <input type="text" name="description" value={formData.description} onChange={handleChange}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Brief description" required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Date <span className="text-red-500">*</span>
                </label>
                <input type="datetime-local" name="scheduled_at" value={formData.scheduled_at} onChange={handleChange}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              {/* Assigned To */}
              <div>
  <label className="block text-sm font-medium text-gray-700">Assigned To</label>
  <UserSearchDropdown
    value={formData.assigned_to} // Pass the current value of assigned_to
    onChange={handleUserSelect} // Pass the handler for user selection
  />
</div>


              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select name="activity_status_id" value={formData.activity_status_id} onChange={handleChange}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="1">Pending</option>
                  <option value="2">Completed</option>
                </select>
              </div>

              <div className="flex items-center">
                <input type="checkbox" name="is_follow_up" checked={formData.is_follow_up} onChange={handleChange} className="mr-2" />
                <label className="block text-sm font-medium text-gray-700">Enable Follow-up</label>
              </div>

              {formData.is_follow_up && (
              <div>
                  <label className="block text-sm font-medium text-gray-700">Follow-Up Date</label>
                  <input type="date" name="follow_up_date" value={formData.follow_up_date} onChange={handleChange}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
              </div>

              
              )}
            </div>
                          {/* Notes */}
                          <div>
              <label className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="Additional notes..."
              ></textarea>
            </div>
          
        
            {/*<button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-500">
              {isEditing ? "Save Changes" : "Add Activity"}
            </button>*/}
          </form>
          
        </div>
        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-100 flex justify-end space-x-4">
          <button onClick={onClose} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md">
            Cancel
          </button>
          <button onClick={handleSubmit} className="px-4 py-2 text-white bg-blue-600 rounded-md">
            {isEditing ? "Save Changes" : "Add Activity"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddEditActivityModal;